import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { IActivityIncome, IProductionBudgetCharter, IProductionBudgetActivities } from "./types"
import api from "services/api"

export function useActivityIncomesTracking(
  params?: { session_id: string },
  config?: UseQueryOptions<IActivityIncome[]>
) {
  return useQuery<IActivityIncome[]>(
    ["a-suite", "finance", "pb-actual-income-entries", params],
    config
  )
}

export function useActivityIncomeTracking(
  incomeId: string,
  config?: UseQueryOptions<IActivityIncome>
) {
  return useQuery<IActivityIncome>(
    ["a-suite", "finance", "pb-actual-income-entries", incomeId],
    config
  )
}

export function useCreateActivityIncome(
  config?: UseMutationOptions<unknown, unknown, FormData>
) {
  const queryClient = useQueryClient()
  return useMutation(
    (data) => api.post(`a-suite/finance/pb-actual-income-entries`, data),
    {
      ...config,
      onSuccess(data, vars, conext) {
        config?.onSuccess?.(data, vars, conext)
        queryClient.invalidateQueries([
          "a-suite",
          "finance",
          "pb-actual-income-entries",
        ])
      },
    }
  )
}

export function useUpdateActivityIncome(
  incomeId: string,
  config?: UseMutationOptions<unknown, unknown, FormData>
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, unknown, FormData>(
    (data) => api.post(`a-suite/finance/pb-actual-income-entries/${incomeId}`, data),
    {
      ...config,
      onSuccess(data, vars, context) {
        config?.onSuccess?.(data, vars, context)
        queryClient.invalidateQueries([
          "a-suite",
          "finance",
          "pb-actual-income-entries",
        ])
      },
    }
  )
}

export function useExtProductionBudgetVersions(
  params?: { session_id: string | null },
  config?: UseQueryOptions<(Record<"id", string> & { is_default: boolean, charter_version_id: string })[]>
) {
  return useQuery<(Record<"id", string> & { is_default: boolean, charter_version_id: string })[]>(
    ["tactical", "finance", "production-budgets", "external", "versions", params],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}

export function useExtProductionBudgetActivities(
  params?: any,
  config?: UseQueryOptions<IProductionBudgetActivities[]>
) {
  return useQuery<IProductionBudgetActivities[]>(
    ["tactical", "finance", "production-budgets", "external", "planning", params],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}

export function useExtProductionBudgetAccountCharters(
  versionId: string,
  params?: { parent_type: 'incomes' | 'expenses' },
  config?: UseQueryOptions<IProductionBudgetCharter[]>
) {
  return useQuery<IProductionBudgetCharter[]>(
    ["configuration", "budget", "pb-account-charters", "external", "versions", versionId, "charters", params],
    {
      keepPreviousData: true,
      ...config,
    }
  )
}